/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2019, Bulb Inc. For more information, please email contact@hellobulb.com.
 *          _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
*/

import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslocoModule, TRANSLOCO_SCOPE } from '@jsverse/transloco';
import { AccordionModule, AccordionStyle } from 'primeng/accordion';
import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { DialogModule } from 'primeng/dialog';
import {
  DialogService, DynamicDialogConfig, DynamicDialogModule, DynamicDialogRef,
} from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { TooltipModule, TooltipStyle } from 'primeng/tooltip';
import { MultiSelectModule } from 'primeng/multiselect';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputSwitchModule } from 'primeng/inputswitch';
import { SelectModule } from 'primeng/select';
import { DatePickerModule } from 'primeng/datepicker';
import { SVGIconModule } from '../app/svg-icon/svg-icon.module';
import { availableLangs } from '../app/exports';

import { BulbCommonModule } from '../common/bulb-common.module';
import { UtilsModule } from '../utils/utils.module';

import { AeroAccordionComponent } from './aero-accordion/aero-accordion.component';
import { AeroAccordionTabComponent } from './aero-accordion/aero-accordion-tab/aero-accordion-tab.component';
import { AeroButtonComponent } from './aero-button/aero-button.component';
import { AeroCalendarPickerComponent } from './aero-calendar-picker/aero-calendar-picker.component';
import { AeroDropdownComponent } from './aero-dropdown/aero-dropdown.component';
import { AeroFormComponent } from './aero-form/aero-form.component';
import { AeroImageRadioButtonComponent }
  from './aero-image-radio-group/aero-image-radio-button/aero-image-radio-button.component';
import { AeroImageRadioGroupComponent } from './aero-image-radio-group/aero-image-radio-group.component';
import { AeroInputComponent } from './aero-input/aero-input.component';
import { AeroTagsComponent } from './aero-tags/aero-tags.component';
import { AeroTextareaComponent } from './aero-textarea/aero-textarea.component';
import { AeroTooltipDirective } from './aero-tooltip/aero-tooltip.directive';
import { AeroUnitTileComponent } from './aero-unit-tile/aero-unit-tile.component';
import { DialogComponent } from './aero-dialog/dialog/dialog.component';
import { DialogContentPlaceholderDirective } from './aero-dialog/dialog/dialog-content-placeholder.directive';
import { DialogFooterComponent } from './aero-dialog/dialog-footer/dialog-footer.component';
import { DialogHeaderComponent } from './aero-dialog/dialog-header/dialog-header.component';
import { AeroMultiselectComponent } from './aero-multiselect/aero-multiselect.component';
import { AeroInputNumberComponent } from './aero-input-number/aero-input-number.component';
import { AeroToggleComponent } from './aero-toggle/aero-toggle.component';

// Primeng button, and dynamic dialog module we are going to wrap:

const loader = availableLangs.reduce((acc, lang) => {
  acc[lang] = () => import(`../resources/translations/locale-${lang}.json`);
  return acc;
}, {});

/**
 * Our Angular Feature Module for our styling library, Aero.
 */
@NgModule({
  declarations: [
    AeroAccordionComponent,
    AeroAccordionTabComponent,
    AeroButtonComponent,
    AeroCalendarPickerComponent,
    AeroDropdownComponent,
    AeroFormComponent,
    AeroImageRadioGroupComponent,
    AeroImageRadioButtonComponent,
    AeroInputComponent,
    AeroInputNumberComponent,
    AeroMultiselectComponent,
    AeroTagsComponent,
    AeroTextareaComponent,
    AeroToggleComponent,
    AeroTooltipDirective,
    AeroUnitTileComponent,
    DialogComponent,
    DialogContentPlaceholderDirective,
    DialogFooterComponent,
    DialogHeaderComponent,
  ],
  providers: [
    DialogService,
    DynamicDialogConfig,
    DynamicDialogRef,
    {
      provide: TRANSLOCO_SCOPE,
      useValue: {
        scope: 'ajs',
        loader,
      },
    },
    TooltipStyle,
    AccordionStyle,
  ],
  exports: [
    AeroAccordionComponent,
    AeroAccordionTabComponent,
    AeroButtonComponent,
    AeroCalendarPickerComponent,
    AeroDropdownComponent,
    AeroFormComponent,
    AeroImageRadioGroupComponent,
    AeroImageRadioButtonComponent,
    AeroInputComponent,
    AeroInputNumberComponent,
    AeroMultiselectComponent,
    AeroTagsComponent,
    AeroTextareaComponent,
    AeroToggleComponent,
    AeroTooltipDirective,
    AeroUnitTileComponent,
    DialogFooterComponent,
    DialogHeaderComponent,
  ],
  imports: [
    AccordionModule,
    BulbCommonModule,
    ButtonModule,
    CalendarModule,
    CommonModule,
    DatePickerModule,
    DialogModule,
    SelectModule,
    DynamicDialogModule,
    FormsModule,
    SVGIconModule,
    InputTextModule,
    TextareaModule,
    TooltipModule,
    TranslocoModule,
    UtilsModule,
    MultiSelectModule,
    InputNumberModule,
    InputSwitchModule,
  ],
})
export class AeroModule { }
